import cx from 'classnames'
import * as React from 'react'

import Icon from '../icon'
import type { Icons } from '../icon/icon'
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
  SpacingProps,
} from '../shared-types'
import Spacer from '../spacer'
import styles from './banner.module.scss'

export type Props = {
  status: 'info' | 'info-light' | 'warning' | 'error'
  icon?: Icons
  children: React.ReactNode
  className?: string
} & SpacingProps

type BannerProps<C extends React.ElementType> =
  PolymorphicComponentPropsWithRef<C, Props>

type BannerComponent = (<C extends React.ElementType = 'div'>(
  props: BannerProps<C>,
) => React.ReactElement | null) & { displayName?: string }

const Banner: BannerComponent = React.forwardRef(
  <C extends React.ElementType>(
    props: BannerProps<C>,
    ref?: PolymorphicRef<C>,
  ) => {
    const { children, icon, status, className, ...otherProps } = props

    const classNames = cx(className, styles.root, styles[`status-${status}`])

    return (
      <Spacer
        {...(otherProps as React.ComponentProps<typeof Spacer>)}
        ref={ref}
        className={classNames}
      >
        {icon && (
          <Icon
            icon={icon}
            size={24}
            color="currentColor"
            marginRight="small"
          />
        )}

        <span>{children}</span>
      </Spacer>
    )
  },
)

Banner.displayName = 'Banner'

export default Banner
